export type LiveMapBase = "satellite" | "roadmap";
/** When site geofences are drawn: only close in (the old behavior), at any zoom, or never. */
export type LiveMapGeofences = "zoomed" | "always" | "off";
/** Which units a clicked site draws lines to; see site-connections. */
export type LiveMapSiteLinks = "nearest" | "headed" | "off";

/** What the live map draws and over which base; per user, safe to persist. */
export type LiveMapDisplayOptions = {
  baseMap: LiveMapBase;
  /** Draws road lines and street labels over the satellite imagery; the road map always has them. */
  streetOverlay: boolean;
  /** Live traffic speeds on the roads, over either base. */
  traffic: boolean;
  /** Fades units whose last ping is older than `staleAfterMinutes`. */
  dimStale: boolean;
  staleAfterMinutes: number;
  /** A ring around each unit showing how full it is, in its cargo's color; see unit-load-ring. */
  unitLoads: boolean;
  /** Names under the unit markers, once zoomed in past `LIVE_MAP_LABEL_MIN_ZOOM`. */
  labels: boolean;
  geofences: LiveMapGeofences;
  /** A badge on each site with its loads ready to haul, red when a container there is critical. */
  siteLoads: boolean;
  siteLinks: LiveMapSiteLinks;
  /** How far "nearest" looks, in miles. */
  siteLinkRadiusMiles: number;
  /** Follows the roads, with drive time, for the closest few instead of drawing straight lines. */
  siteRoadRoutes: boolean;
};

export const DEFAULT_LIVE_MAP_DISPLAY: LiveMapDisplayOptions = {
  baseMap: "satellite",
  streetOverlay: false,
  traffic: false,
  dimStale: true,
  staleAfterMinutes: 30,
  unitLoads: true,
  labels: false,
  geofences: "zoomed",
  siteLoads: true,
  siteLinks: "nearest",
  siteLinkRadiusMiles: 25,
  siteRoadRoutes: false,
};

export const LIVE_MAP_STALE_MINUTES = [15, 30, 60, 240] as const;
/** Below this zoom the labels would pile into each other. */
export const LIVE_MAP_LABEL_MIN_ZOOM = 11;
/** Below this zoom "zoomed" geofences are hidden, as they always were. */
export const LIVE_MAP_GEOFENCE_MIN_ZOOM = 15;

const BASES: LiveMapBase[] = ["satellite", "roadmap"];
const GEOFENCES: LiveMapGeofences[] = ["zoomed", "always", "off"];
const SITE_LINKS: LiveMapSiteLinks[] = ["nearest", "headed", "off"];
export const LIVE_MAP_SITE_LINK_RADII = [10, 25, 50] as const;

/**
 * Saved options over the defaults, dropping anything malformed, so a newer default reaches older saves. Each app
 * passes its own defaults, e.g. DispatchGo labels its units and HubGo does not.
 */
export const normalizeLiveMapDisplay = (
  saved: unknown,
  defaults: LiveMapDisplayOptions = DEFAULT_LIVE_MAP_DISPLAY,
): LiveMapDisplayOptions => {
  const display = {...defaults};
  if (!saved || typeof saved !== "object") return display;
  const values = saved as Record<string, unknown>;
  if (BASES.includes(values.baseMap as LiveMapBase)) display.baseMap = values.baseMap as LiveMapBase;
  if (GEOFENCES.includes(values.geofences as LiveMapGeofences)) display.geofences = values.geofences as LiveMapGeofences;
  if (SITE_LINKS.includes(values.siteLinks as LiveMapSiteLinks)) display.siteLinks = values.siteLinks as LiveMapSiteLinks;
  if ((LIVE_MAP_SITE_LINK_RADII as readonly unknown[]).includes(values.siteLinkRadiusMiles)) {
    display.siteLinkRadiusMiles = values.siteLinkRadiusMiles as number;
  }
  if ((LIVE_MAP_STALE_MINUTES as readonly unknown[]).includes(values.staleAfterMinutes)) {
    display.staleAfterMinutes = values.staleAfterMinutes as number;
  }
  for (const key of ["streetOverlay", "traffic", "dimStale", "unitLoads", "labels", "siteLoads", "siteRoadRoutes"] as const) {
    if (typeof values[key] === "boolean") display[key] = values[key] as boolean;
  }
  return display;
};

/** Google's hybrid type is the satellite base with the road map's roads and labels on top. */
export const liveMapTypeId = (display: LiveMapDisplayOptions): "hybrid" | LiveMapBase =>
  display.baseMap === "satellite" && display.streetOverlay ? "hybrid" : display.baseMap;

/** Whether a unit's last ping is old enough to fade; a unit with no ping is left alone. */
export const isLiveMapPositionStale = (pingedAt: number | undefined, display: LiveMapDisplayOptions, now: number) =>
  display.dimStale && pingedAt != null && now - pingedAt >= display.staleAfterMinutes * 60_000;

export const showLiveMapLabels = (display: LiveMapDisplayOptions, zoom: number | undefined) =>
  display.labels && zoom != null && zoom >= LIVE_MAP_LABEL_MIN_ZOOM;

export const showLiveMapGeofences = (display: LiveMapDisplayOptions, zoom: number | undefined) =>
  display.geofences === "always" || (display.geofences === "zoomed" && zoom != null && zoom >= LIVE_MAP_GEOFENCE_MIN_ZOOM);

/** "15 min", "1 h", "4 h": how the stale threshold reads in the menu and on a faded marker. */
export const formatStaleMinutes = (minutes: number) => minutes < 60 ? `${minutes} min` : `${minutes / 60} h`;

export const LIVE_MAP_DISPLAY_STORAGE_KEY = "ots.live-map.display.v1";

export const readLiveMapDisplay = (
  key = LIVE_MAP_DISPLAY_STORAGE_KEY,
  defaults: LiveMapDisplayOptions = DEFAULT_LIVE_MAP_DISPLAY,
): LiveMapDisplayOptions => {
  try {
    return normalizeLiveMapDisplay(JSON.parse(localStorage.getItem(key) ?? "null"), defaults);
  } catch {
    return defaults;
  }
};

export const saveLiveMapDisplay = (display: LiveMapDisplayOptions, key = LIVE_MAP_DISPLAY_STORAGE_KEY) => {
  try {
    localStorage.setItem(key, JSON.stringify(display));
  } catch {
    // Private browsing or a full quota: the choice simply lasts for this visit.
  }
};
